import * as THREE from 'three';
import { FOG_FRAG, FOG_VERT, fxUniforms, quadGeometry } from './common';
import { noiseTexture } from './noise';

export const enum Shape {
  Glow = 0, Spark = 1, Ring = 2, Floor = 3, Beam = 4, Shard = 5, Flare = 6, Flame = 7, Frost = 8, Sun = 9, Venom = 10, Bubble = 11, Arc = 12, FlameJet = 13, Burn = 14, IceSpike = 15,
  /** Volumetric fire: noise-eroded, temperature-ramped, cools into smoke when `body` > 0. */
  Fire = 16,
  /** Occluding smoke / mist puff (alpha-blended; `body` is its opacity, colour is its tint). */
  Smoke = 17,
  /** Liquid droplet stretched along its velocity: dark body, rim and a specular glint. */
  Drop = 18,
  /** Ground-aligned liquid splat that dries away. */
  Splat = 19,
  /** Stationary curved blood ribbon, with pulses running back along its length. */
  BloodThread = 20,
  /** Ground-aligned clock face with tick marks and a motionless hand. */
  Clock = 21,
}

export interface Emit {
  x: number; y: number; z: number;
  vx?: number; vy?: number; vz?: number;
  color: THREE.Color;
  /** HDR multiplier; > 1 blooms. */
  intensity?: number;
  life: number;
  size: number; sizeEnd?: number;
  gravity?: number;
  /** Exponential velocity damping (1/s). */
  drag?: number;
  shape?: Shape;
  /** Spark: streak length per unit of screen velocity. Beam: height. */
  stretch?: number;
  /** Alpha fade curve exponent over life. */
  fade?: number;
  /** Seconds before the particle appears (staggered bursts without timers). */
  delay?: number;
  /**
   * Occlusion for Fire/Smoke/Drop/Splat (0 = purely additive light). Blending is
   * premultiplied, so light and occluding smoke or liquid share one draw call.
   */
  body?: number;
}

/**
 * Stateless GPU particles. Each particle is written once at emission with its
 * initial conditions; the vertex shader integrates position analytically
 * (drag + gravity), so the CPU does zero per-particle work per frame.
 * All shapes share one additive draw call.
 */
export class Particles {
  readonly mesh: THREE.Mesh;
  private readonly a: THREE.InstancedBufferAttribute[];
  private readonly p0: Float32Array; private readonly v0: Float32Array;
  private readonly c: Float32Array; private readonly s: Float32Array; private readonly k: Float32Array;
  private cursor = 0;
  /** Slot where this frame's emissions began, and how many were written. */
  private start = 0;
  private pending = 0;
  time = 0;

  constructor(readonly capacity = 8192) {
    const g = quadGeometry();
    const mk = (name: string) => {
      const attr = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
      g.setAttribute(name, attr);
      return attr;
    };
    this.a = ['aP', 'aV', 'aC', 'aS', 'aK'].map(mk);
    [this.p0, this.v0, this.c, this.s, this.k] = this.a.map((x) => x.array as Float32Array);
    // Park every slot in the past so unused particles are culled in the shader.
    for (let i = 0; i < capacity; i++) { this.p0[i * 4 + 3] = -1e6; this.v0[i * 4 + 3] = 1; }
    g.instanceCount = capacity;

    const mat = new THREE.ShaderMaterial({
      uniforms: { ...fxUniforms, uNoise: { value: noiseTexture() } },
      // premultiplied alpha: rgb is added, alpha occludes. Alpha 0 is exactly additive.
      transparent: true, depthWrite: false, blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor,
      side: THREE.DoubleSide, forceSinglePass: true,
      vertexShader: /* glsl */ `
        attribute vec4 aP; attribute vec4 aV; attribute vec4 aC; attribute vec4 aS; attribute vec4 aK;
        uniform float uTime;
        varying vec2 vUv; varying vec3 vCol; varying float vShape, vAge;
        varying vec4 vRaw; varying float vT, vSeed, vBody;
        ${FOG_VERT}
        void main() {
          float age = uTime - aP.w, life = aV.w, t = age / life;
          if (t < 0.0 || t > 1.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
          float drag = aS.w, g = aS.z;
          float e = exp(-drag * age);
          float k = drag > 0.0 ? (1.0 - e) / drag : age;
          vec3 p = aP.xyz + aV.xyz * k - vec3(0.0, 0.5 * g * age * age, 0.0);
          vec3 vel = aV.xyz * e - vec3(0.0, g * age, 0.0);
          if (aK.x == 12.0 || aK.x == 13.0 || aK.x == 20.0) { p = aP.xyz; vel = aV.xyz; }
          float size = mix(aS.x, aS.y, t);
          float shape = aK.x;
          vShape = shape;
          vAge = age;
          vUv = uv * 2.0 - 1.0;
          vCol = aC.rgb * aC.a * pow(1.0 - t, aK.z) * min(1.0, age * 40.0 + 0.3);
          vRaw = vec4(aC.rgb, aC.a * min(1.0, age * 30.0 + 0.2)); vT = t; vBody = aK.w;
          vSeed = fract(sin(dot(aP.xyz, vec3(12.9898, 78.233, 37.719)) + aP.w * 91.7) * 43758.5453);
          vec4 mv;
          if (shape == 2.0 || shape == 3.0 || shape == 14.0 || shape == 19.0 || shape == 21.0 || (shape >= 8.0 && shape <= 10.0)) {
            // ground-aligned (rings, floor flashes)
            mv = modelViewMatrix * vec4(p + vec3(position.x, 0.0, -position.y) * size, 1.0);
          } else if (shape == 4.0 || shape == 15.0) {
            // vertical beam: cylindrical billboard
            vec3 right = normalize(vec3(viewMatrix[0][0], 0.0, viewMatrix[2][0]));
            float growth = shape == 15.0 ? smoothstep(0.0,0.12,age) : 1.0;
            vec3 wp = p + right * position.x * size + vec3(0.0, (position.y + 0.5) * aK.y * growth, 0.0);
            mv = modelViewMatrix * vec4(wp, 1.0);
          } else {
            mv = modelViewMatrix * vec4(p, 1.0);
            if (shape == 1.0 || shape == 5.0 || shape == 12.0 || shape == 13.0 || shape == 16.0 || shape == 18.0 || shape == 20.0) {
              vec2 sv = (viewMatrix * vec4(vel, 0.0)).xy;
              float sl = length(sv);
              vec2 dir = sl > 1e-4 ? sv / sl : vec2(0.0, 1.0);
              // Keep the quad's winding: the opposite perpendicular mirrors it
              // and the front-face culler removes every stretched particle.
              vec2 perp = vec2(dir.y, -dir.x);
              // fire stands upright when slow and stretches into a tongue along fast motion
              float len = size * (shape == 16.0 ? 1.3 : 1.0) + sl * aK.y;
              mv.xy += dir * position.y * len + perp * position.x * size * (shape == 1.0 ? 0.35 : shape >= 16.0 ? 1.0 : 0.8);
            } else {
              mv.xy += position.xy * vec2(size, size * (shape == 7.0 ? 1.65 : shape == 16.0 ? 1.3 : 1.0));
            }
          }
          FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying vec2 vUv; varying vec3 vCol; varying float vShape, vAge;
        varying vec4 vRaw; varying float vT, vSeed, vBody;
        ${FOG_FRAG}
        float h2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float vn(vec2 p) {
          vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h2(i), h2(i + vec2(1.0, 0.0)), f.x), mix(h2(i + vec2(0.0, 1.0)), h2(i + vec2(1.0, 1.0)), f.x), f.y);
        }
        // fire, smoke and splats read the shared tiling noise instead of hashing 8 lattice corners per pixel
        uniform sampler2D uNoise;
        // two reads, same octave layout as the hashed fbm (×1 and ×2.13, weights .62/.38), remapped to its
        // distribution (mean .50, σ .157; the texture pair gives mean .494, σ .092)
        float fbm(vec2 p) {
          float n = texture2D(uNoise, p * 0.125).r * 0.62 + texture2D(uNoise, (p * 2.13 + 1.7) * 0.125).g * 0.38;
          return (n - 0.494) * 1.707 + 0.5;
        }
        void main() {
          float fog = fogKeep();
          if (vShape >= 16.0 && vShape <= 19.0) {
            vec2 uv = vUv;
            vec3 rgb = vec3(0.0); float alpha = 0.0;
            if (vShape == 16.0) {
              // FIRE: a soft body eroded by upward-scrolling noise; the erosion threshold
              // rises with age, and the surviving density picks a temperature from the ramp.
              float r = length(uv * vec2(1.0, 0.82) - vec2(0.0, -0.12));
              vec2 np = uv * 1.5 + vec2(vSeed * 17.0, -vAge * 3.4);
              np.x += sin(uv.y * 3.0 + vAge * 9.0 + vSeed * 6.0) * 0.22;
              float n = fbm(np * 1.35);
              float shape = 1.0 - smoothstep(0.3, 1.0, r);
              float dens = shape * (0.42 + 0.66 * n);
              float heat = clamp((dens - vT * 0.75) * 1.45, 0.0, 1.0);
              float a = smoothstep(0.0, 0.14, heat);
              vec3 tint = vRaw.rgb;
              // temperature ramp: soot red → element tint → yellow → white only at the very core.
              // Per-puff energy stays low so overlapping puffs build a hot core instead of a white blob.
              // warm tints burn through yellow; cold or violet tints (soul-fire) stay in their hue toward white
              float cold = smoothstep(-0.1, 0.3, tint.b - tint.g);
              vec3 soot = mix(vec3(0.3, 0.025, 0.008), tint * 0.25, cold);
              vec3 hot = mix(vec3(1.0, 0.62, 0.22), mix(tint, vec3(1.0), 0.28), cold);
              vec3 c = mix(soot, tint, smoothstep(0.08, 0.4, heat));
              c = mix(c, hot, smoothstep(0.45, 0.8, heat));
              c = mix(c, vec3(1.0, 0.93, 0.8) + vec3(0.0, 0.0, 0.15) * cold, smoothstep(0.86, 1.0, heat));
              rgb = c * (0.22 + heat * heat * 1.05) * a * vRaw.a;
              // cooling into smoke: dark, occluding, in the wisps the flame has already left
              float smoke = vBody * smoothstep(0.2, 0.75, vT) * shape * (0.35 + 0.65 * n) * (1.0 - smoothstep(0.75, 1.0, vT));
              smoke *= 1.0 - a * 0.7;
              rgb += vec3(0.045, 0.036, 0.03) * smoke;
              alpha = smoke;
            } else if (vShape == 17.0) {
              // SMOKE: rotating, slowly churning puff, lit from above, eroding with age
              float ang = vSeed * 6.283 + vAge * (vSeed - 0.5) * 0.9, cs = cos(ang), sn = sin(ang);
              vec2 ru = mat2(cs, sn, -sn, cs) * uv;
              float n = fbm(ru * 1.6 + vSeed * 7.0 + vec2(0.0, -vAge * 0.35));
              float d = length(uv), shape = exp(-d * d * 2.6) * (1.0 - smoothstep(0.85, 1.0, d));
              // billows: the noise carves the puff so it never reads as a disc, and eats it as it ages
              float dens = shape * (0.2 + 1.1 * n * n) - vT * 0.45;
              float a = smoothstep(0.08, 0.5, dens) * vBody * min(1.0, vRaw.a);
              vec3 c = vRaw.rgb * (0.7 + 0.55 * (uv.y * 0.5 + 0.5));
              rgb = c * a; alpha = a;
            } else if (vShape == 18.0) {
              // DROP: teardrop along velocity (head leads), dark liquid body, bright rim, glint
              float wide = mix(0.45, 1.0, smoothstep(-1.0, 0.55, uv.y));
              float r = length(vec2(uv.x / wide, uv.y));
              float a = (1.0 - smoothstep(0.72, 1.0, r)) * (1.0 - smoothstep(0.75, 1.0, vT));
              float rim = smoothstep(0.45, 0.95, r);
              float glint = exp(-length(uv - vec2(-0.32, 0.38)) * 7.0);
              rgb = (vRaw.rgb * (0.28 + rim * 0.9) + vec3(1.0, 1.0, 0.92) * glint * 1.4) * a * vRaw.a;
              alpha = a * vBody;
            } else {
              // SPLAT: ragged liquid stain with a wet rim and glints, drying from the edge in
              float ang = atan(uv.y, uv.x);
              float r = length(uv) + (fbm(uv * 2.2 + vSeed * 9.0) - 0.5) * 0.4 + sin(ang * 7.0 + vSeed * 30.0) * 0.04;
              float edge = 0.86 - vT * 0.35;
              float a = 1.0 - smoothstep(edge - 0.08, edge, r);
              float rim = smoothstep(edge - 0.25, edge - 0.04, r) * a;
              float glint = pow(max(0.0, fbm(uv * 3.0 + vSeed * 3.0) - 0.55) * 2.2, 3.0);
              rgb = (vRaw.rgb * (0.22 + rim * 0.9) + vec3(0.9, 1.0, 0.8) * glint * 0.6) * a * vRaw.a;
              alpha = a * vBody * (1.0 - smoothstep(0.7, 1.0, vT));
            }
            gl_FragColor = vec4(rgb * fog, alpha * fog);
            return;
          }
          float a, hot = 0.0;
          vec3 tint = vec3(1.0);
          if (vShape == 20.0) {
            float along = vUv.y * 0.5 + 0.5;
            float curve = sin(along * 6.2832 + vSeed * 6.0) * 0.42 * sin(along * 3.1416);
            float x = abs(vUv.x - curve);
            float taper = smoothstep(0.0, 0.06, along) * (1.0 - smoothstep(0.94, 1.0, along));
            float pulse = pow(max(0.0, cos(along * 22.0 - vAge * 34.0)), 6.0);
            a = exp(-x * x * 25.0) * taper * (0.3 + pulse * 0.7);
            hot = exp(-x * x * 100.0) * pulse * taper * 0.08;
          } else if (vShape == 21.0) {
            float d = length(vUv), angle = atan(vUv.y, vUv.x);
            float rim = exp(-pow((d - 0.82) * 45.0, 2.0));
            float inner = exp(-pow((d - 0.59) * 60.0, 2.0)) * 0.35;
            float ticks = pow(max(0.0, cos(angle * 12.0)), 24.0) * smoothstep(0.64, 0.69, d) * (1.0 - smoothstep(0.76, 0.81, d));
            float hand = pow(max(0.0, cos(angle - vSeed * 6.2832)), 120.0) * smoothstep(0.04, 0.1, d) * (1.0 - smoothstep(0.49, 0.55, d));
            a = (rim + inner + ticks * 0.9 + hand + exp(-d * d * 200.0)) * (1.0 - smoothstep(0.88, 0.95, d)) * (1.0 - smoothstep(0.82, 1.0, vT));
          } else if (vShape == 1.0) {
            float x = 1.0 - abs(vUv.x), y = 1.0 - abs(vUv.y);
            a = x * x * smoothstep(0.0, 0.6, y) * (0.6 + 0.4 * (vUv.y * 0.5 + 0.5));
          } else if (vShape == 12.0) {
            // lightning segment: short end taper so neighbours overlap into one unbroken arc, white-hot spine
            float x = 1.0 - abs(vUv.x), y = smoothstep(0.0, 0.3, 1.0 - abs(vUv.y));
            a = x * x * y;
            hot = pow(x, 10.0) * y * 0.7;
          } else if (vShape == 13.0) {
            // flame stream: thin hot root, turbulent body that breaks into tongues and cools to red at the tip
            float along = vUv.y * 0.5 + 0.5;
            float wobble = (sin(along * 23.0 - vAge * 61.0) * 0.1 + sin(along * 9.0 + vAge * 27.0) * 0.06) * along;
            float x = abs(vUv.x + wobble) / mix(0.1, 0.9, sqrt(along));
            float body = 1.0 - smoothstep(0.3, 1.0, x);
            float tongues = 0.5 + 0.5 * sin(along * 34.0 - vAge * 70.0 + vUv.x * 5.0);
            a = body * mix(1.0, tongues, smoothstep(0.15, 0.8, along)) * smoothstep(1.0, 0.72, along) * smoothstep(0.0, 0.05, along);
            hot = pow(max(0.0, 1.0 - x), 3.0) * (1.0 - along) * a * 0.9;
            tint = mix(vec3(1.0), vec3(1.0, 0.42, 0.22), smoothstep(0.35, 1.0, max(x, along)));
          } else if (vShape == 15.0) {
            float y = vUv.y*0.5+0.5, width = 1.0-y*0.92;
            a = (1.0-smoothstep(width-0.06,width,abs(vUv.x))) * (vUv.x < 0.0 ? 0.55 : 1.0) * smoothstep(0.0,0.04,y);
          } else if (vShape == 14.0) {
            // scorch mark: branching fissures from warped noise ridges (not a spiral), fading at a torn edge
            float d = length(vUv);
            vec2 w = vUv * 3.0 + vec2(vn(vUv * 2.0 + vSeed * 13.0), vn(vUv * 2.0 + 5.3 + vSeed * 7.0)) * 1.4;
            float crack = smoothstep(0.05, 0.0, abs(vn(w + vSeed * 31.0) - 0.5));
            float edgeD = d + (vn(vUv * 3.0 + vSeed * 17.0) - 0.5) * 0.3;
            a = (0.06 + crack*0.6) * (1.0-smoothstep(0.45,0.95,edgeD));
          } else if (vShape == 2.0) {
            float d = length(vUv); float w = (d - 0.86) / 0.09;
            a = exp(-w * w) + 0.12 * smoothstep(1.0, 0.0, d) * d;
            if (d > 1.0) a = 0.0;
          } else if (vShape == 4.0) {
            float x = 1.0 - abs(vUv.x);
            a = x * x * x * smoothstep(1.0, 0.2, vUv.y) * smoothstep(-1.0, -0.8, vUv.y);
          } else if (vShape == 5.0) {
            // hard-edged diamond shard
            float d = abs(vUv.x) + abs(vUv.y);
            a = smoothstep(1.0, 0.8, d) * (0.55 + 0.45 * (1.0 - abs(vUv.x)));
          } else if (vShape == 6.0) {
            // four-point star glint
            float x = abs(vUv.x), y = abs(vUv.y);
            a = exp(-x * 16.0) * exp(-y * 2.6) + exp(-y * 16.0) * exp(-x * 2.6) + exp(-length(vUv) * 6.0) * 0.8;
            a *= 1.0 - smoothstep(0.85, 1.0, max(x, y));
          } else if (vShape == 7.0) {
            float y = vUv.y * 0.5 + 0.5;
            float x = vUv.x + sin(y * 12.0 - vAge * 18.0) * y * 0.18;
            float width = mix(0.75, 0.04, y);
            a = (1.0 - smoothstep(width * 0.35, width, abs(x))) * sin(y * 3.14159);
          } else if (vShape == 8.0) {
            float d = length(vUv), angle = atan(vUv.y, vUv.x);
            float hex = d * cos(mod(angle + 0.5236, 1.0472) - 0.5236);
            float rim = exp(-pow((hex - 0.74) * 25.0, 2.0));
            float branches = exp(-abs(sin(angle * 3.0)) * 55.0) * (1.0 - smoothstep(0.15, 1.0, d));
            float forks = exp(-abs(sin(angle * 3.0 + d * 14.0)) * 48.0) * (1.0 - smoothstep(0.35, 0.85, d)) * 0.35;
            a = (rim * 0.5 + branches + forks) * (1.0 - smoothstep(0.92, 1.0, d));
          } else if (vShape == 9.0) {
            float d = length(vUv), angle = atan(vUv.y, vUv.x);
            float rays = pow(max(0.0, cos(angle * 6.0)), 10.0) * exp(-d * 2.4);
            float ring = exp(-pow((d - 0.6) * 24.0, 2.0));
            a = (rays + ring * 0.5) * (1.0 - smoothstep(0.9, 1.0, d));
          } else if (vShape == 10.0) {
            float angle = atan(vUv.y, vUv.x);
            float d = length(vUv) + sin(angle * 5.0 + vAge * 2.0) * 0.07 + sin(angle * 9.0 - vAge) * 0.045;
            float pool = (1.0 - smoothstep(0.25, 0.93, d)) * 0.2;
            float rim = exp(-pow((d - 0.7) * 13.0, 2.0)) * 0.6;
            a = (pool + rim) * (1.0 - smoothstep(0.93, 1.0, d));
          } else if (vShape == 11.0) {
            float d = length(vUv);
            a = exp(-pow((d - 0.68) * 15.0, 2.0)) * 0.7 + exp(-length(vUv - vec2(-0.2, 0.28)) * 16.0);
            a *= 1.0 - smoothstep(0.9, 1.0, d);
          } else {
            float d = length(vUv);
            a = exp(-3.6 * d) * (1.0 - min(d * d, 1.0));
          }
          float peak = max(vCol.r, max(vCol.g, vCol.b));
          gl_FragColor = vec4((vCol * tint * a + vec3(peak * hot)) * fog, 0.0);
        }`,
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
  }

  emit(e: Emit) {
    const i = this.cursor, j = i * 4;
    this.cursor = (this.cursor + 1) % this.capacity;
    this.pending++;
    this.p0[j] = e.x; this.p0[j + 1] = e.y; this.p0[j + 2] = e.z; this.p0[j + 3] = this.time + (e.delay ?? 0);
    this.v0[j] = e.vx ?? 0; this.v0[j + 1] = e.vy ?? 0; this.v0[j + 2] = e.vz ?? 0; this.v0[j + 3] = e.life;
    this.c[j] = e.color.r; this.c[j + 1] = e.color.g; this.c[j + 2] = e.color.b; this.c[j + 3] = e.intensity ?? 1;
    this.s[j] = e.size; this.s[j + 1] = e.sizeEnd ?? e.size; this.s[j + 2] = e.gravity ?? 0; this.s[j + 3] = e.drag ?? 0;
    this.k[j] = e.shape ?? Shape.Glow; this.k[j + 1] = e.stretch ?? 0; this.k[j + 2] = e.fade ?? 1.2; this.k[j + 3] = e.body ?? 0;
  }

  /** Upload this frame's emissions: one range, or the tail and head of the ring after a wrap. */
  flush() {
    const n = Math.min(this.pending, this.capacity);
    if (!n) return;
    const start = n === this.capacity ? 0 : this.start, end = start + n;
    for (const a of this.a) {
      a.clearUpdateRanges();
      a.addUpdateRange(start * 4, (Math.min(end, this.capacity) - start) * 4);
      if (end > this.capacity) a.addUpdateRange(0, (end - this.capacity) * 4);
      a.needsUpdate = true;
    }
    this.start = this.cursor; this.pending = 0;
  }
}
